import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserPlus, Phone } from 'lucide-react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';

export default function Register() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('As palavras-passe não coincidem');
    }

    try {
      setError('');
      const userCredential = await register(email, password);
      
      // Set initial user data with pending approval status
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        email,
        fullName,
        companyName,
        phone: phone.startsWith('+351') ? phone : `+351${phone}`,
        role: 'gerente',
        approvalStatus: 'pending',
        createdAt: new Date().toISOString(),
        onboardingCompleted: false
      });

      setShowOnboarding(true);
    } catch (err: any) {
      console.error('Registration error:', err);
      if (err.code === 'auth/email-already-in-use') {
        setError('Este email já está registado. Por favor, use outro email ou faça login.');
      } else {
        setError('Falha ao criar conta. Por favor, tente novamente.');
      }
    }
  }

  if (showOnboarding) {
    return <OnboardingFlow />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary-light to-secondary flex items-center justify-center px-4">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg">
        <div className="text-center">
          <img
            src="/logo_lognow_preto.svg"
            alt="LOGNOW"
            className="mx-auto h-12 w-auto"
          />
          <h2 className="mt-6 text-3xl font-bold text-secondary">Criar Conta</h2>
        </div>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-gray-700">
                Nome Completo
              </label>
              <input
                id="fullName"
                type="text"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Introduza o seu nome completo"
              />
            </div>
            
            <div>
              <label htmlFor="companyName" className="block text-sm font-medium text-gray-700">
                Nome da Empresa
              </label>
              <input
                id="companyName"
                type="text"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                placeholder="Introduza o nome da sua empresa"
              />
            </div>
            
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                Contacto Telefónico
              </label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Phone className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </div>
                <div className="absolute inset-y-0 left-8 pl-3 flex items-center pointer-events-none">
                  <span className="text-gray-500 sm:text-sm">+351</span>
                </div>
                <input
                  id="phone"
                  type="tel"
                  required
                  className="pl-24 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                  value={phone}
                  onChange={(e) => {
                    // Remove qualquer prefixo +351 que o utilizador possa ter digitado
                    const value = e.target.value.replace(/^\+351/, '');
                    // Permitir apenas dígitos
                    const digitsOnly = value.replace(/\D/g, '');
                    setPhone(digitsOnly);
                  }}
                  placeholder="912345678"
                />
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Introduza apenas os 9 dígitos do seu número português
              </p>
            </div>
            
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="exemplo@empresa.pt"
              />
            </div>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Palavra-passe
              </label>
              <input
                id="password"
                type="password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700">
                Confirmar Palavra-passe
              </label>
              <input
                id="confirm-password"
                type="password"
                required
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </div>
          </div>

          <button
            type="submit"
            className="w-full flex justify-center items-center gap-2 py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-secondary bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
          >
            <UserPlus size={20} />
            Registar
          </button>

          <div className="text-center">
            <Link to="/login" className="text-primary hover:text-primary-dark">
              Já tem conta? Entre aqui
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}